import { useState } from "react";
import Modal from "../components/Modal";
import Field, { inputStyle, selectStyle } from "../components/Field";
import Badge from "../components/Badge";
import { CONTRACT_STATUSES, SERVICES } from "../constants";
import { fmt, fmtDate } from "../utils";

export default function ContractDetail({ contract, onClose, setContracts, today }) {
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});

  if (!contract) return null;

  // Compute real status
  let computedStatus = contract.status;
  if (computedStatus === "ativo" && contract.endDate) {
    const daysLeft = Math.ceil((new Date(contract.endDate) - new Date(today)) / 86400000);
    if (daysLeft < 0) computedStatus = "vencido";
    else if (daysLeft <= 30) computedStatus = "vencendo";
  }
  const daysToEnd = contract.endDate ? Math.ceil((new Date(contract.endDate) - new Date(today)) / 86400000) : null;
  const st = CONTRACT_STATUSES.find((s) => s.id === computedStatus);

  const startEdit = () => {
    setEditData({
      type: contract.type, value: String(contract.value || ""),
      startDate: contract.startDate || "", endDate: contract.endDate || "",
      renewalDate: contract.renewalDate || "", notes: contract.notes || "",
      status: contract.status,
    });
    setEditing(true);
  };

  const saveEdit = () => {
    setContracts((prev) => prev.map((c) =>
      c.id === contract.id ? { ...c, ...editData, value: parseFloat(editData.value) || 0 } : c
    ));
    setEditing(false);
    onClose();
  };

  const cancelContract = () => {
    setContracts((prev) => prev.map((c) =>
      c.id === contract.id ? { ...c, status: "cancelado" } : c
    ));
    onClose();
  };

  return (
    <Modal open={!!contract} onClose={onClose} title="Detalhe do Contrato" w={560}>
      {editing ? (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0 12px" }}>
            <Field label="Tipo">
              <select style={selectStyle} value={editData.type} onChange={(e) => setEditData((p) => ({ ...p, type: e.target.value }))}>
                <option value="mensal">Mensal</option>
                <option value="semestral">Semestral</option>
                <option value="anual">Anual</option>
              </select>
            </Field>
            <Field label="Valor Mensal (R$)">
              <input style={inputStyle} type="number" step="0.01" value={editData.value} onChange={(e) => setEditData((p) => ({ ...p, value: e.target.value }))} />
            </Field>
            <Field label="Início">
              <input style={inputStyle} type="date" value={editData.startDate} onChange={(e) => setEditData((p) => ({ ...p, startDate: e.target.value }))} />
            </Field>
            <Field label="Vencimento">
              <input style={inputStyle} type="date" value={editData.endDate} onChange={(e) => setEditData((p) => ({ ...p, endDate: e.target.value }))} />
            </Field>
            <Field label="Renovação">
              <input style={inputStyle} type="date" value={editData.renewalDate} onChange={(e) => setEditData((p) => ({ ...p, renewalDate: e.target.value }))} />
            </Field>
            <Field label="Status">
              <select style={selectStyle} value={editData.status} onChange={(e) => setEditData((p) => ({ ...p, status: e.target.value }))}>
                {CONTRACT_STATUSES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
              </select>
            </Field>
            <div style={{ gridColumn: "1/-1" }}>
              <Field label="Observações">
                <textarea style={{ ...inputStyle, minHeight: 50, resize: "vertical" }} value={editData.notes} onChange={(e) => setEditData((p) => ({ ...p, notes: e.target.value }))} />
              </Field>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 8 }}>
            <button onClick={() => setEditing(false)} style={{ padding: "7px 14px", borderRadius: 6, border: "1px solid #e2e8f0", background: "#fff", color: "#64748b", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Cancelar</button>
            <button onClick={saveEdit} style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: "#251C59", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Salvar</button>
          </div>
        </div>
      ) : (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
            <div>
              <div style={{ fontSize: 18, fontWeight: 800, color: "#0f172a" }}>{contract.clientName}</div>
              <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                Contrato {contract.type === "mensal" ? "Mensal" : contract.type === "anual" ? "Anual" : "Semestral"}
              </div>
            </div>
            <Badge color={st?.color}>{st?.label}</Badge>
          </div>

          {/* Status alert */}
          {computedStatus === "vencendo" && (
            <div style={{ padding: "10px 14px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a", marginBottom: 14, fontSize: 12, color: "#92400e" }}>
              Vence em <strong>{daysToEnd} dias</strong> — programe a renovação!
            </div>
          )}
          {computedStatus === "vencido" && (
            <div style={{ padding: "10px 14px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", marginBottom: 14, fontSize: 12, color: "#991b1b" }}>
              Contrato vencido há <strong>{Math.abs(daysToEnd)} dias</strong>
            </div>
          )}

          {/* Info grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 16 }}>
            {[
              { label: "Valor Mensal", value: fmt(contract.value || 0) },
              { label: "Início", value: fmtDate(contract.startDate) },
              { label: "Vencimento", value: fmtDate(contract.endDate) },
              { label: "Renovação", value: fmtDate(contract.renewalDate) },
              { label: "Criado em", value: fmtDate(contract.createdAt) },
              { label: "Dias Restantes", value: daysToEnd !== null ? (daysToEnd > 0 ? `${daysToEnd}d` : "Vencido") : "—" },
            ].map((item, i) => (
              <div key={i} style={{ padding: 10, borderRadius: 7, background: "#f8fafc", textAlign: "center" }}>
                <div style={{ fontSize: 10, color: "#64748b" }}>{item.label}</div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", marginTop: 2 }}>{item.value}</div>
              </div>
            ))}
          </div>

          {/* Services */}
          {contract.services?.length > 0 && (
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", marginBottom: 6, textTransform: "uppercase" }}>Serviços Contratados</div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {contract.services.map((s) => {
                  const svc = SERVICES.find((x) => x.id === s);
                  return <Badge key={s} color="#251C59" small>{svc?.label || s}</Badge>;
                })}
              </div>
            </div>
          )}

          {/* Notes */}
          {contract.notes && (
            <div style={{ padding: "10px 14px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 12, color: "#78350f", lineHeight: 1.4, marginBottom: 16 }}>
              <strong>Observações:</strong> {contract.notes}
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            <button onClick={startEdit} style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: "#251C59", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
              Editar
            </button>
            {contract.status !== "cancelado" && (
              <button onClick={cancelContract} style={{ padding: "7px 14px", borderRadius: 6, border: "none", background: "#ef4444", color: "#fff", fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                ✕ Cancelar Contrato
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
